import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    // Run animation on next animation frame
    requestAnimationFrame(() => {
      this.#animateTrajectory();
    });
  }

  #getTopHeaderCartIcon() {
    const isCartPage =
      document.body.classList.contains('template-cart') ||
      (window.location.pathname.endsWith('/cart') || window.location.pathname.endsWith('/cart/'));

    // 1. If on cart page, target the Cart title / bubble in top-left
    if (isCartPage) {
      const cartBubble = document.querySelector('.cart-title .cart-bubble, .cart-title [data-testid="cart-bubble"], .cart-title h1, .cart-page__title h1');
      if (cartBubble) {
        const r = cartBubble.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return cartBubble;
      }
    }

    // 2. Check collection facets nav actions (top-right on collection/shop-all pages)
    const collectionCart = document.querySelector(
      '.collection-facets-nav__actions cart-icon, .collection-facets-nav__actions .header-actions__cart-icon'
    );
    if (collectionCart) {
      const r = collectionCart.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return collectionCart;
    }

    // 3. Check inside top header
    const headerCart = document.querySelector(
      'header cart-icon, header .header-actions__cart-icon, .header cart-icon, .header .header-actions__cart-icon, #header-group cart-icon'
    );
    if (headerCart) {
      const r = headerCart.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return headerCart;
    }

    // 4. Query all cart icons and find visible one furthest on top-right
    const allCartIcons = Array.from(document.querySelectorAll('cart-icon, .header-actions__cart-icon, [data-testid="cart-icon"], .product-page-cart-link'));
    const topCartIcons = allCartIcons.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.top < 200;
    });

    if (topCartIcons.length > 0) {
      topCartIcons.sort((a, b) => b.getBoundingClientRect().left - a.getBoundingClientRect().left);
      return topCartIcons[0];
    }

    return allCartIcons[0] || null;
  }

  #animateTrajectory = () => {
    const isCartPage =
      document.body.classList.contains('template-cart') ||
      (window.location.pathname.endsWith('/cart') || window.location.pathname.endsWith('/cart/'));
    const sourceEl = this.source;
    let destEl = null;

    if (isCartPage) {
      destEl =
        document.querySelector('.cart-title .cart-bubble, .cart-title [data-testid="cart-bubble"], .cart-title h1, .cart-page__title h1');
    }

    if (!destEl) {
      destEl = this.#getTopHeaderCartIcon() || this.destination;
    }

    if (!sourceEl && !this.startCoords && !this.sourceRect) {
      this.remove();
      return;
    }

    const sourceRect =
      this.sourceRect && this.sourceRect.width > 0
        ? this.sourceRect
        : sourceEl
          ? sourceEl.getBoundingClientRect()
          : null;
    const destRect = destEl ? destEl.getBoundingClientRect() : null;

    // Exact center coordinates of source button
    let startX = this.startCoords?.x;
    let startY = this.startCoords?.y;

    if (startX === undefined || startY === undefined) {
      if (sourceRect && (sourceRect.width > 0 || sourceRect.height > 0)) {
        startX = sourceRect.left + sourceRect.width / 2;
        startY = sourceRect.top + sourceRect.height / 2;
      } else {
        startX = window.innerWidth / 2;
        startY = window.innerHeight / 2;
      }
    }

    // Exact center coordinates of cart target
    let targetX;
    let targetY;

    if (isCartPage) {
      const bubbleEl =
        document.querySelector('.cart-title .cart-bubble, .cart-title [data-testid="cart-bubble"], .cart-title h1, .cart-page__title h1');

      if (bubbleEl) {
        destEl = bubbleEl;
        const bRect = bubbleEl.getBoundingClientRect();
        if (bRect.width > 0 && bRect.width < 120) {
          targetX = bRect.left + bRect.width / 2;
          targetY = bRect.top >= 0 ? bRect.top + bRect.height / 2 : 36;
        } else if (bRect.width >= 120) {
          targetX = bRect.left + 75;
          targetY = bRect.top >= 0 ? bRect.top + bRect.height / 2 : 36;
        } else {
          targetX = 180;
          targetY = 36;
        }
      } else if (destRect && destRect.width > 0) {
        targetX = destRect.left + (destRect.width > 100 ? 75 : destRect.width / 2);
        targetY = destRect.top >= 0 ? destRect.top + destRect.height / 2 : 36;
      } else {
        targetX = 180;
        targetY = 36;
      }

      // Hard clamp: on Cart page, target is on top left near title
      if (targetX > window.innerWidth * 0.45) {
        const cartH1 = document.querySelector('.cart-title h1, .cart-page__title h1');
        if (cartH1) {
          const hRect = cartH1.getBoundingClientRect();
          targetX = hRect.left + 75;
        } else {
          targetX = 180;
        }
      }
    } else {
      const topCart = this.#getTopHeaderCartIcon();
      if (topCart) {
        destEl = topCart;
      }
      const dRect = destEl ? destEl.getBoundingClientRect() : null;

      targetX = dRect && dRect.width > 0 ? dRect.left + dRect.width / 2 : window.innerWidth - 45;
      targetY = dRect && dRect.height > 0 ? dRect.top + dRect.height / 2 : 28;

      // Ensure target is in the top right quadrant
      if (targetX < window.innerWidth / 2) {
        targetX = window.innerWidth - 45;
      }
      if (targetY > 200 || targetY < 0) {
        targetY = 28;
      }
    }

    // Differences
    const deltaX = targetX - startX;
    const deltaY = targetY - startY;

    // Position bubble initially at the exact center of source button
    this.style.position = 'fixed';
    this.style.left = `${startX}px`;
    this.style.top = `${startY}px`;
    this.style.transform = 'translate(-50%, -50%)';
    this.style.zIndex = '999999';
    this.style.pointerEvents = 'none';
    this.style.opacity = '1';

    // Set bubble dimensions (44px default)
    if (this.useSourceSize && sourceRect && sourceRect.width > 0) {
      const size = Math.min(Math.max(sourceRect.width, 36), 60);
      this.style.width = `${size}px`;
      this.style.height = `${size}px`;
    }

    // Smooth curved trajectory using Web Animations API (920ms for smooth, clear tracking)
    const animation = this.animate(
      [
        {
          transform: 'translate(-50%, -50%) translate3d(0, 0, 0) scale(1)',
          opacity: 1
        },
        {
          transform: `translate(-50%, -50%) translate3d(${deltaX * 0.35}px, ${deltaY * 0.58}px, 0) scale(0.88)`,
          opacity: 0.98,
          offset: 0.38
        },
        {
          transform: `translate(-50%, -50%) translate3d(${deltaX * 0.76}px, ${deltaY * 0.86}px, 0) scale(0.6)`,
          opacity: 0.9,
          offset: 0.72
        },
        {
          transform: `translate(-50%, -50%) translate3d(${deltaX * 0.94}px, ${deltaY * 0.97}px, 0) scale(0.3)`,
          opacity: 0.75,
          offset: 0.92
        },
        {
          transform: `translate(-50%, -50%) translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.05)`,
          opacity: 0
        }
      ],
      {
        duration: 920,
        easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)',
        fill: 'forwards'
      }
    );

    animation.onfinish = () => {
      // 1. Trigger radial particle celebration pop effect
      this.#triggerCartPopBurst(targetX, targetY);

      // 2. Scale/bounce effect on destination Cart icon or Cart Title
      if (destEl) {
        destEl.animate(
          [
            { transform: 'scale(1)' },
            { transform: 'scale(1.28)' },
            { transform: 'scale(0.94)' },
            { transform: 'scale(1.06)' },
            { transform: 'scale(1)' }
          ],
          {
            duration: 400,
            easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }
        );
      }
      this.remove();
    };
  };

  /**
   * Spawns a radial burst of small beads/sparkles around the Cart icon (Celebration pop!)
   * @param {number} centerX - Cart icon X coordinate
   * @param {number} centerY - Cart icon Y coordinate
   */
  #triggerCartPopBurst = (centerX, centerY) => {
    const particleCount = 14;
    const colors = ['#FF4D6D', '#FFB703', '#06D6A0', '#118AB2', '#7209B7', '#4CC9F0', '#FFD166', '#FF007F'];
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '9999999';
    document.body.appendChild(container);

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      const color = colors[i % colors.length] || '#FF4D6D';
      const size = Math.random() * 2.5 + 3.5; // 3.5px - 6px
      const angle = (i / particleCount) * 2 * Math.PI + (Math.random() - 0.5) * 0.35;
      const distance = Math.random() * 18 + 24; // 24px - 42px radial distance
      const destX = Math.cos(angle) * distance;
      const destY = Math.sin(angle) * distance;

      particle.style.position = 'fixed';
      particle.style.left = `${centerX}px`;
      particle.style.top = `${centerY}px`;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.borderRadius = '50%';
      particle.style.backgroundColor = color;
      particle.style.boxShadow = `0 0 5px ${color}`;
      particle.style.pointerEvents = 'none';
      particle.style.transform = 'translate(-50%, -50%)';

      container.appendChild(particle);

      particle.animate(
        [
          {
            transform: 'translate(-50%, -50%) translate3d(0, 0, 0) scale(0.6)',
            opacity: 1
          },
          {
            transform: `translate(-50%, -50%) translate3d(${destX * 0.6}px, ${destY * 0.6}px, 0) scale(1.25)`,
            opacity: 1,
            offset: 0.38
          },
          {
            transform: `translate(-50%, -50%) translate3d(${destX}px, ${destY}px, 0) scale(0.2)`,
            opacity: 0
          }
        ],
        {
          duration: Math.random() * 100 + 400, // 400ms - 500ms
          easing: 'cubic-bezier(0.12, 0.8, 0.32, 1)',
          fill: 'forwards'
        }
      );
    }

    setTimeout(() => {
      container.remove();
    }, 550);
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
